// Copyright 2020 Drivetrain Hub LLC
// GraphQL handlers for tool data.

import {CUT_GENERATING_PINION_INFO, CUT_GENERATING_RACK_INFO} from "./gear";
import {mutateUnits} from "./units";
import helper from "./helper";
import lib from "./lib";

// region BASIC RACK

const BASIC_RACK_GEOM_INFO = {
  // <param prop>: [<description>, <symbol>, <unit type>]
  // PLACEHOLDER {symbols} verify symbols from ISO standard
  moduleNormal: CUT_GENERATING_RACK_INFO['moduleNormal'],
  pressureAngle: CUT_GENERATING_RACK_INFO['pressureAngleNormal'],
  addendumCoefficient: CUT_GENERATING_RACK_INFO['addendumCoefficient'],
  dedendumCoefficient: CUT_GENERATING_RACK_INFO['dedendumCoefficient'],
  rootRadiusCoefficient: ['Root radius coefficient', '$\\rho_f^*$', 'none'],
  // skipping rootHeightLimit
  undercutAngle: ['Undercut angle', '$\\alpha_{fp}$', 'angle'],  // symbol?
  undercutSize: ['Undercut size', '$U_{fp}$', 'length'],  // symbol?
};
const BASIC_RACK_GEOM_KEYS = Object.keys(BASIC_RACK_GEOM_INFO);

function lookupBasicRackGeomInfo(paramStr) {
  return helper.lookupParamInfo(BASIC_RACK_GEOM_INFO, BASIC_RACK_GEOM_KEYS, paramStr);
}

/**
 * Convert units of basic rack properties.
 * @param {Object} basicRack - Basic rack object.  Function mutates this object.
 * @param {Object} fromUnits - Units to convert from.  Defaults to unit system default.
 * @param {Object} toUnits - Units to convert to.  Defaults to unit system default.
 * @return {Object} Mutated input object.
 */
function convertBasicRackGeom(basicRack, fromUnits, toUnits) {
  mutateUnits(basicRack.geom, BASIC_RACK_GEOM_INFO, fromUnits, toUnits);
  return basicRack;
}

/**
 * Create an object for tabulating basic rack geometry parameter data.
 * @param {Object} basicRack - Basic rack object.
 * @param {Object} units - Optional labeling for units.  Units are *not* be converted by this function.
 * @return {{parameter: string, symbol: string, value: number || string, units: string}[]} Array with props required by LibTableParams.vue.
 */
function tableBasicRackGeom(basicRack, units) {
  return lib.libTableParams(basicRack.geom, lookupBasicRackGeomInfo, units);
}

/**
 * Create an array for tabulating basic rack geometry parameter data in an el-table element.
 * @param {Object} units - Optional labeling for units.  Units are *not* be converted by this function.
 * @return {*[]} Array of objects containing props required by LibTableFilter.vue.
 */
function basicRackFilterTable(units) {

  function colLabel(paramStr) {
    return helper.formatElTableLabel(lookupBasicRackGeomInfo, paramStr, units)
  }

  // colData props API:  label, prop, formatter, minWidth, align

  return [{
    label: 'Name',
    prop: 'name',
    minWidth: '140',
  }, {
    label: 'Number',
    prop: 'number',
    minWidth: '80',
  }, {
    label: colLabel('moduleNormal'),
    prop: 'geom.moduleNormal',
    formatter: helper.formatElTableValue,
    align: 'center',
  }, {
    label: colLabel('pressureAngle'),
    prop: 'geom.pressureAngle',
    formatter: helper.formatElTableValue,
    align: 'center',
  }, {
    label: colLabel('addendumCoefficient'),
    prop: 'geom.addendumCoefficient',
    formatter: helper.formatElTableValue,
    align: 'center',
  }, {
    label: colLabel('dedendumCoefficient'),
    prop: 'geom.dedendumCoefficient',
    formatter: helper.formatElTableValue,
    align: 'center',
  }];
}

// endregion

// region HOB

const HOB_GEOM_INFO = {
  // <param prop>: [<description>, <symbol>, <unit type>]
  // PLACEHOLDER {symbols} verify symbols from ISO standard
  // gear-cutting params
  moduleNormal: CUT_GENERATING_RACK_INFO['moduleNormal'],
  pressureAngleNormal: CUT_GENERATING_RACK_INFO['pressureAngleNormal'],
  addendumCoefficient: CUT_GENERATING_RACK_INFO['addendumCoefficient'],
  dedendumCoefficient: CUT_GENERATING_RACK_INFO['dedendumCoefficient'],
  tipRadiusCoefficient: CUT_GENERATING_RACK_INFO['tipRadiusCoefficient'],
  protuberanceAngle: CUT_GENERATING_RACK_INFO['protuberanceAngle'],
  protuberanceSize: CUT_GENERATING_RACK_INFO['protuberanceSize'],
  activeLength: ['Active length', '', 'length'],
  numberOfFlutes: ['Number of flutes', '', 'none'],
  numberOfThreads: ['Number of threads', '', 'none'],
  pitchDiameter: ['Pitch diameter', '', 'length'],
  threadHelixHand: ['Thread helix hand', '', 'none'],
};
const HOB_GEOM_KEYS = Object.keys(HOB_GEOM_INFO);

function lookupHobGeomInfo(paramStr) {
  return helper.lookupParamInfo(HOB_GEOM_INFO, HOB_GEOM_KEYS, paramStr);
}

/**
 * Convert units of hob properties.
 * @param {Object} hob - Hob object.  Function mutates this object.
 * @param {Object} fromUnits - Units to convert from.  Defaults to unit system default.
 * @param {Object} toUnits - Units to convert to.  Defaults to unit system default.
 * @return {Object} Mutated input object.
 */
function convertHobGeom(hob, fromUnits, toUnits) {
  mutateUnits(hob.geom, HOB_GEOM_INFO, fromUnits, toUnits);
  return hob;
}

/**
 * Create an object for tabulating hob geometry parameter data.
 * @param {Object} hob - Hob object.
 * @param {Object} units - Optional labeling for units.  Units are *not* be converted by this function.
 * @return {{parameter: string, symbol: string, value: number || string, units: string}[]} Array with props required by LibTableParams.vue.
 */
function tableHobGeom(hob, units) {
  return lib.libTableParams(hob.geom, lookupHobGeomInfo, units);
}

/**
 * Create an array for tabulating hob geometry parameter data in an el-table element.
 * @param {Object} units - Optional labeling for units.  Units are *not* be converted by this function.
 * @return {*[]} Array of objects containing props required by LibTableFilter.vue.
 */
function hobFilterTable(units) {

  function colLabel(paramStr) {
    return helper.formatElTableLabel(lookupHobGeomInfo, paramStr, units)
  }

  // colData props API:  label, prop, formatter, minWidth, align

  return [{
    label: 'Name',
    prop: 'name',
    minWidth: '140',
  }, {
    label: 'Number',
    prop: 'number',
    minWidth: '80',
  }, {
    label: colLabel('moduleNormal'),
    prop: 'geom.moduleNormal',
    formatter: helper.formatElTableValue,
    align: 'center',
  }, {
    label: colLabel('pressureAngleNormal'),
    prop: 'geom.pressureAngleNormal',
    formatter: helper.formatElTableValue,
    align: 'center',
  }, {
    label: colLabel('addendumCoefficient'),
    prop: 'geom.addendumCoefficient',
    formatter: helper.formatElTableValue,
    align: 'center',
  }, {
    label: colLabel('dedendumCoefficient'),
    prop: 'geom.dedendumCoefficient',
    formatter: helper.formatElTableValue,
    align: 'center',
  }];
}

// endregion

// region PINION CUTTER

const PINION_CUTTER_GEOM_INFO = {
  // <param prop>: [<description>, <symbol>, <unit type>]
  // PLACEHOLDER {symbols} verify symbols from ISO standard
  // gear-cutting params
  moduleNormal: CUT_GENERATING_PINION_INFO['moduleNormal'],
  numberOfTeeth: CUT_GENERATING_PINION_INFO['numberOfTeeth'],
  pressureAngleNormal: CUT_GENERATING_PINION_INFO['pressureAngleNormal'],
  helixAngle: CUT_GENERATING_PINION_INFO['helixAngle'],
  addendumCoefficient: CUT_GENERATING_PINION_INFO['addendumCoefficient'],
  dedendumCoefficient: CUT_GENERATING_PINION_INFO['dedendumCoefficient'],
  tipRadiusCoefficient: CUT_GENERATING_PINION_INFO['tipRadiusCoefficient'],
  protuberanceSize: CUT_GENERATING_PINION_INFO['protuberanceSize'],
};
const PINION_CUTTER_GEOM_KEYS = Object.keys(PINION_CUTTER_GEOM_INFO);

function lookupPinionCutterGeomInfo(paramStr) {
  return helper.lookupParamInfo(PINION_CUTTER_GEOM_INFO, PINION_CUTTER_GEOM_KEYS, paramStr);
}

/**
 * Convert units of pinion cutter properties.
 * @param {Object} pinionCutter - PinionCutter object.  Function mutates this object.
 * @param {Object} fromUnits - Units to convert from.  Defaults to unit system default.
 * @param {Object} toUnits - Units to convert to.  Defaults to unit system default.
 * @return {Object} Mutated input object.
 */
function convertPinionCutterGeom(pinionCutter, fromUnits, toUnits) {
  mutateUnits(pinionCutter.geom, PINION_CUTTER_GEOM_INFO, fromUnits, toUnits);
  return pinionCutter;
}

/**
 * Create an object for tabulating pinion cutter geometry parameter data.
 * @param {Object} pinionCutter - PinionCutter object.
 * @param {Object} units - Optional labeling for units.  Units are *not* be converted by this function.
 * @return {{parameter: string, symbol: string, value: number || string, units: string}[]} Array with props required by LibTableParams.vue.
 */
function tablePinionCutterGeom(pinionCutter, units) {
  return lib.libTableParams(pinionCutter.geom, lookupPinionCutterGeomInfo, units);
}

/**
 * Create an array for tabulating pinion cutter geometry parameter data in an el-table element.
 * @param {Object} units - Optional labeling for units.  Units are *not* be converted by this function.
 * @return {*[]} Array of objects containing props required by LibTableFilter.vue.
 */
function pinionCutterFilterTable(units) {

  function colLabel(paramStr) {
    return helper.formatElTableLabel(lookupPinionCutterGeomInfo, paramStr, units)
  }

  // colData props API:  label, prop, formatter, minWidth, align

  return [{
    label: 'Name',
    prop: 'name',
    minWidth: '140',
  }, {
    label: 'Number',
    prop: 'number',
    minWidth: '80',
  }, {
    label: colLabel('moduleNormal'),
    prop: 'geom.moduleNormal',
    formatter: helper.formatElTableValue,
    align: 'center',
  }, {
    label: colLabel('pressureAngleNormal'),
    prop: 'geom.pressureAngleNormal',
    formatter: helper.formatElTableValue,
    align: 'center',
  }, {
    label: colLabel('addendumCoefficient'),
    prop: 'geom.addendumCoefficient',
    formatter: helper.formatElTableValue,
    align: 'center',
  }, {
    label: colLabel('helixAngle'),
    prop: 'geom.helixAngle',
    formatter: helper.formatElTableValue,
    align: 'center',
  }];
}

// endregion

export default {
  // BASIC RACK
  convertBasicRackGeom: convertBasicRackGeom,
  tableBasicRackGeom: tableBasicRackGeom,
  basicRackFilterTable: basicRackFilterTable,
  // HOB
  convertHobGeom: convertHobGeom,
  tableHobGeom: tableHobGeom,
  hobFilterTable: hobFilterTable,
  // PINION CUTTER
  convertPinionCutterGeom: convertPinionCutterGeom,
  tablePinionCutterGeom: tablePinionCutterGeom,
  pinionCutterFilterTable: pinionCutterFilterTable,
}
